import {announce} from '@github-ui/aria-live'
import {IssueViewer} from '@github-ui/issue-viewer/IssueViewer'
import {
  ISSUE_VIEWER_DEFAULT_CONFIG,
  type IssueViewerPreloadedQueries,
  type OptionConfig,
} from '@github-ui/issue-viewer/OptionConfig'
import type {ItemIdentifier} from '@github-ui/issue-viewer/Types'
import {accountIssueOptionFromPathname} from '@github-ui/issue-viewer/Urls'
import type {AppPayload} from '@github-ui/issues-shared/app-payload'
import {IssueSidePanel} from '@github-ui/issues-shared/IssueSidePanel'
import {LABELS} from '@github-ui/issues-shared/labels'
import {noop} from '@github-ui/noop'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
/* eslint-disable-next-line @github-ui/github-monorepo/no-use-app-payload --
   useAppPayload is deprecated. See
   https://thehub.github.com/epd/engineering/dev-practicals/frontend/react/data-router/migration/app-payload-deprecation/ */
import {useAppPayload} from '@github-ui/react-core/use-app-payload'
import {useLocation} from '@github-ui/react-router'
import {RelayCachePreheatContextProvider} from '@github-ui/relay-query-cache/contexts/RelayCachePreheatContext'
import {useHovercardClickIntercept} from '@github-ui/use-link-interception/use-hovercard-click-intercept'
import {useSessionStorage} from '@github-ui/use-safe-storage/session-storage'
import {AlertIcon, ScreenFullIcon} from '@primer/octicons-react'
import {IconButton, Link} from '@primer/react'
import {clsx} from 'clsx'
import type React from 'react'
import {lazy, Suspense, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react'
import {graphql, useFragment} from 'react-relay'

import {MESSAGES} from '../../constants/messages'
import {TEST_IDS} from '../../constants/test-ids'
import {EMPTY_VIEW} from '../../constants/view-constants'
import {useAppNavigate} from '../../hooks/use-app-navigate'
import {type SidePanelItemIdentifier, useRouteInfo} from '../../hooks/use-route-info'
import {useViewNav} from '../../hooks/use-view-nav'
import {isUrlInRepoIssuesContext, timelineEventBaseUrl} from '../../utils/urls'
import type {IssueDetailCurrentViewFragment$key} from './__generated__/IssueDetailCurrentViewFragment.graphql'
import styles from './IssueDetail.module.css'

// Account issues require the TanStack viewer, so load it only when one opens in the side panel.
const TanStackIssueViewer = lazy(async () => {
  const mod = await import('@github-ui/issue-viewer/TanStackIssueViewer')
  return {default: mod.TanStackIssueViewer}
})

function IssueLoadingFallback() {
  const ref = useRef<HTMLSpanElement>(null)

  useEffect(() => {
    if (ref.current) announce(MESSAGES.loadingIssue, {element: ref.current})
  }, [])

  return <span ref={ref}>{MESSAGES.loadingIssue}...</span>
}

function AccountIssueLoadError({number, permalink}: {number: number; permalink: string}) {
  useEffect(() => announce(MESSAGES.issueLoadError, {assertive: true}), [])

  return (
    <div className="d-flex flex-column flex-items-start gap-2 p-4 f5 color-fg-muted">
      <span className="d-inline-flex flex-items-center gap-2">
        <AlertIcon className="color-fg-attention" />
        {MESSAGES.issueLoadError}
      </span>
      <Link href={permalink}>{MESSAGES.openIssue(number)}</Link>
    </div>
  )
}

type IssueDetailProps = {
  fetchedView: IssueDetailCurrentViewFragment$key
  preloadedQueries: IssueViewerPreloadedQueries
}

export const IssueDetail = ({fetchedView, preloadedQueries}: IssueDetailProps) => {
  const {pathname} = useLocation()
  const {
    itemIdentifier,
    viewId,
    sidePanelItemIdentifier,
    sidePanelIssueDeletionConnection,
    setSidePanelItemIdentifier,
    sidePanelItemURL,
  } = useRouteInfo()
  const {onIssueHrefLinkClick, navigateToRoot} = useAppNavigate()
  const [subIssueFromOptimisticUpdate, setSubIssueFromOptimisticUpdate] = useState(false)
  const activeSidePanelRef = useRef<SidePanelItemIdentifier | null>(null)
  useLayoutEffect(() => {
    activeSidePanelRef.current = sidePanelItemIdentifier
    return () => {
      activeSidePanelRef.current = null
    }
  }, [sidePanelItemIdentifier])
  const {viewNavState, onLoadMore, onLoadBack} = useViewNav()
  const {query: viewQuery} = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment IssueDetailCurrentViewFragment on Shortcutable {
        name
        query
      }
    `,
    fetchedView, // check for null above
  )

  const appPayload = useAppPayload<AppPayload>()
  const showRepositoryPill = !appPayload.scoped_repository

  const navigateBack = useCallback(() => {
    navigateToRoot(viewId ?? EMPTY_VIEW.id, viewQuery)
  }, [viewId, viewQuery, navigateToRoot])
  const {navigateToUrl} = useAppNavigate()

  const onSubIssueClick = useCallback<NonNullable<OptionConfig['onSubIssueClick']>>(
    (subIssueItem, options) => {
      if (options?.fromSuccessfulOptimisticUpdate) {
        setSubIssueFromOptimisticUpdate(true)
      }
      const {owner, repo, number} = subIssueItem
      activeSidePanelRef.current = null
      setSidePanelItemIdentifier({owner, repo, number, type: 'Issue'}, options?.issueDeletionConnection)
    },
    [setSidePanelItemIdentifier],
  )
  const onCloseSidePanel = useCallback(() => {
    activeSidePanelRef.current = null
    setSidePanelItemIdentifier(null)
  }, [setSidePanelItemIdentifier])
  const onDeletedSidePanel = useCallback(() => {
    // DELETE can outlive its dialog. Each open creates a new identifier, including reopening the same issue.
    if (!sidePanelItemIdentifier || activeSidePanelRef.current !== sidePanelItemIdentifier) return
    onCloseSidePanel()
  }, [onCloseSidePanel, sidePanelItemIdentifier])

  const [_, setDeletedRecord] = useSessionStorage<string>(
    `${appPayload.scoped_repository?.owner}-${appPayload.scoped_repository?.name}-deletedRecordId`,
    '',
  )

  const onIssueDelete = useCallback(
    (issueId: string) => {
      setDeletedRecord(issueId)
    },
    [setDeletedRecord],
  )

  const onParentIssueActivate = useCallback(
    (e: React.MouseEvent | React.KeyboardEvent, parentIdentifier: ItemIdentifier): boolean => {
      if (!itemIdentifier) return false

      // Only close the side panel if the parent is the same as the current issue
      if (
        parentIdentifier.owner !== itemIdentifier.owner ||
        parentIdentifier.repo !== itemIdentifier.repo ||
        parentIdentifier.number !== itemIdentifier.number
      ) {
        return false
      }

      e.preventDefault()
      onCloseSidePanel()
      return true
    },
    [itemIdentifier, onCloseSidePanel],
  )

  useHovercardClickIntercept((targetUrl, event) => {
    // Soft navigation works only for issues in the same repo
    if (isUrlInRepoIssuesContext(targetUrl, itemIdentifier?.owner || '', itemIdentifier?.repo || '')) {
      return
    }

    const link = (event.target as HTMLElement).closest('a')
    if (!link) return

    navigateToUrl(link)
  })

  const scopedRepo = appPayload.scoped_repository
  const copilotSurveyBanner = appPayload.copilotSurveyBanner ?? undefined

  const primaryOptionConfig = useMemo(
    () => ({
      showRepositoryPill,
      showIssueCreateButton: true,
      onIssueUpdate: noop,
      onLinkClick: onIssueHrefLinkClick,
      onRemovedFromProject: noop,
      navigateBack,
      onIssueDelete,
      accountIssueOption: accountIssueOptionFromPathname(pathname),
      navigate: navigateToUrl,
      preloadedQueries,
      timelineEventBaseUrl: timelineEventBaseUrl(scopedRepo?.name, scopedRepo?.owner),
      scrollToTopOnClick: true,
      onSubIssueClick,
      useViewportQueries: true, // This is a full page view, so we want to use viewport queries
      copilotSurveyBanner,
      /*
       * Toggling between false and undefined allows us to distinguish if the side panel is open or not
       * from the full page view without adding an additional prop
       */
      insideSidePanel: sidePanelItemIdentifier ? false : undefined,
      viewNavState,
      onLoadMoreNavItems: onLoadMore,
      onLoadBackNavItems: onLoadBack,
    }),
    [
      showRepositoryPill,
      onIssueHrefLinkClick,
      navigateBack,
      onIssueDelete,
      pathname,
      navigateToUrl,
      preloadedQueries,
      onSubIssueClick,
      scopedRepo?.name,
      scopedRepo?.owner,
      sidePanelItemIdentifier,
      viewNavState,
      onLoadMore,
      onLoadBack,
      copilotSurveyBanner,
    ],
  )

  const sidePanelOptionConfig: OptionConfig | undefined = sidePanelItemIdentifier
    ? {
        ...ISSUE_VIEWER_DEFAULT_CONFIG,
        shouldSkipSetDocumentTitle: true,
        insideSidePanel: true,
        subIssueFromOptimisticUpdate,
        onIssueDelete,
        issueDeletionConnection: sidePanelIssueDeletionConnection,
        accountIssueOption: sidePanelItemIdentifier.isAccountIssue
          ? {isAccountIssue: true, permalink: sidePanelItemIdentifier.permalink}
          : undefined,
        showRepositoryPill:
          sidePanelItemIdentifier.isAccountIssue ||
          sidePanelItemIdentifier.owner !== itemIdentifier?.owner ||
          sidePanelItemIdentifier.repo !== itemIdentifier?.repo,
        additionalHeaderActions: (
          <IconButton
            as="a"
            role="link"
            variant="invisible"
            icon={ScreenFullIcon}
            aria-label={LABELS.sidePanelTooltip}
            href={sidePanelItemURL}
          />
        ),
      }
    : undefined

  return (
    <>
      {/* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */}
      <div className={clsx('primary-viewer', styles.IssueDetailContainer)} data-testid={TEST_IDS.issueViewerContainer}>
        <Suspense fallback={<IssueLoadingFallback />}>
          {itemIdentifier && (
            <RelayCachePreheatContextProvider>
              <IssueViewer itemIdentifier={itemIdentifier} optionConfig={primaryOptionConfig} />
            </RelayCachePreheatContextProvider>
          )}
        </Suspense>
      </div>
      {sidePanelItemIdentifier && sidePanelOptionConfig && (
        <IssueSidePanel onClose={onCloseSidePanel}>
          {sidePanelItemIdentifier.isAccountIssue ? (
            <ErrorBoundary
              key={sidePanelItemIdentifier.permalink}
              boundaryName="IssueDetailAccountSidePanel"
              fallback={<AccountIssueLoadError {...sidePanelItemIdentifier} />}
            >
              <Suspense fallback={<IssueLoadingFallback />}>
                <TanStackIssueViewer
                  {...sidePanelItemIdentifier}
                  optionConfig={{
                    ...sidePanelOptionConfig,
                    onClose: onCloseSidePanel,
                    onSubIssueClick,
                    onParentIssueActivate,
                    navigateBack: onDeletedSidePanel,
                  }}
                />
              </Suspense>
            </ErrorBoundary>
          ) : (
            <IssueViewer
              itemIdentifier={sidePanelItemIdentifier}
              optionConfig={{
                ...sidePanelOptionConfig,
                onClose: onCloseSidePanel,
                onSubIssueClick,
                onParentIssueActivate,
                navigateBack: onDeletedSidePanel,
              }}
            />
          )}
        </IssueSidePanel>
      )}
    </>
  )
}
